import React, { useState, useEffect, useMemo } from 'react';
import { Tabs, theme } from 'antd';
import classNames from 'classnames';
import './index.less';

export interface HashTabItem {
  /** 显示文案 */
  label: React.ReactNode;
  /** 业务唯一 key */
  key: string;
  /** 路由路径（hash 值，如 'list' 或 '#list'） */
  path?: string;
  /** 路由对应的组件 */
  element: React.ReactNode;
}

export interface HashTabsProps {
  /** 选项列表 */
  items: HashTabItem[];
  /** 自定义类名（可选） */
  className?: string;
  /** 内容区额外样式（可选） */
  contentStyle?: React.CSSProperties;
  /** 默认重定向到哪个 key（可选，默认取第一个） */
  defaultRedirect?: string;
}

const HashTabs: React.FC<HashTabsProps> = ({ 
  items, 
  className, 
  contentStyle,
  defaultRedirect 
}) => {
  const { token } = theme.useToken();
  
  // 从 hash 获取当前激活的 key
  const getHashKey = (): string => {
    const hash = window.location.hash.replace(/^#/, '').replace(/^\//, '');
    if (!hash) {
      return defaultRedirect || items[0]?.key;
    }
    const match = items.find((it) => {
      const itemPath = it.path || it.key;
      return hash === itemPath;
    });
    return match?.key ?? (defaultRedirect || items[0]?.key);
  };

  const [activeKey, setActiveKey] = useState<string>(getHashKey());

  // 监听 hash 变化
  useEffect(() => {
    const handleHashChange = () => {
      setActiveKey(getHashKey());
    };

    window.addEventListener('hashchange', handleHashChange);
    // 初始化时检查 hash；如果没有 hash，使用第一项（或 defaultRedirect）并使用 replace 更新 URL，避免产生历史记录
    if (!window.location.hash) {
      const firstKey = defaultRedirect || items[0]?.key;
      const firstItem = items.find((it) => it.key === firstKey) || items[0];
      const hashPath = firstItem?.path || firstItem?.key;
      if (hashPath) {
        const base = window.location.pathname + window.location.search;
        window.location.replace(`${base}#${hashPath}`);
        setActiveKey(firstItem.key);
      }
    } else {
      setActiveKey(getHashKey());
    }

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [items, defaultRedirect]);

  // 当前显示的组件
  const currentElement = useMemo(() => {
    const currentItem = items.find((it) => it.key === activeKey);
    return currentItem?.element ?? items[0]?.element;
  }, [items, activeKey]);

  // 处理 tab 切换
  const handleTabChange = (key: string) => {
    const target = items.find((it) => it.key === key);
    if (target) {
      const hashPath = target.path || target.key;
      window.location.hash = hashPath;
      setActiveKey(key);
    }
  };

  return (
    <>
      <Tabs
        className={classNames('hash-tabs', className)}
        activeKey={activeKey}
        onChange={handleTabChange}
        items={items.map((it) => ({ key: it.key, label: it.label }))}
      />
      <div style={{ marginTop: token.margin, ...contentStyle }}>
        {currentElement}
      </div>
    </>
  );
};

export default HashTabs;


